import { Box, FormControl, Input, Table, Typography } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import WrappedButton from "../WrappedButton";

export interface ProposalMissingBuilding {
	InvoiceNumber: string;
	CustomerName: string;
	InvoiceDate: string;
	TotalAmount: number;
	Description: string;
	InvoiceHeaderID: string;
}

export default function ProposalsMissingBuilding() {
	const [data, setData] = React.useState<Array<ProposalMissingBuilding>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [loadingPdfId, setLoadingPdfId] = React.useState<string | null>(null);
	const [hidingId, setHidingId] = React.useState<string | null>(null);

	React.useEffect(() => {
		getData();
	}, []);

	const getData = () => {
		fetch("Proposal/GetProposalsMissingBuilding", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							InvoiceNumber: y.invoice_number,
							CustomerName: y.building_name,
							InvoiceDate: y.invoice_date,
							TotalAmount: y.total_amount,
							Description: y.description,
							InvoiceHeaderID: y.invoice_header_id,
						} as ProposalMissingBuilding;
					})
				);
			});
	};

	const openPdf = (proposal: ProposalMissingBuilding) => {
		setLoadingPdfId(proposal.InvoiceHeaderID);
		fetch("Proposal/GetPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNum: proposal.InvoiceNumber,
				isPackingList: false,
				invoiceHeaderId: proposal.InvoiceHeaderID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			})
			.finally(() => {
				setLoadingPdfId(null);
			});
	};

	// Hides a proposal from the missing building list
	const hideProposal = (proposal: ProposalMissingBuilding) => {
		setHidingId(proposal.InvoiceHeaderID);
		fetch("Proposal/HideProposal", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				invoiceHeaderId: proposal.InvoiceHeaderID,
			}),
		})
			.then(() => {
				// Refresh the data after hiding
				getData();
			})
			.finally(() => {
				setHidingId(null);
			});
	};

	const maxPages = Math.ceil((data ?? []).length / 50);
	const thisRows = data
		?.filter(
			(x) =>
				Util.isNullOrEmpty(globalFilter) ||
				x.CustomerName.toLowerCase().includes(globalFilter.toLowerCase()) ||
				x.InvoiceNumber.toString().includes(globalFilter) ||
				x.Description?.toLowerCase().includes(globalFilter.toLowerCase())
		)
		.slice(pageNumber * 50, (pageNumber + 1) * 50);

	return (
		<>
			<Box sx={{ overflowY: "auto", padding: 2 }}>
				<div style={{ display: "flex" }}>
					<div style={{ flex: 1 }}>
						<Typography level="title-lg">Proposals Missing Building</Typography>
					</div>
					<FormControl sx={{ marginRight: 1 }}>
						<Input
							name="Search"
							placeholder="Global Search"
							onChange={(event) => {
								setGlobalFilter(event.target.value);
							}}
							value={globalFilter}
						/>
					</FormControl>
				</div>
				<Table stripe={"odd"} hoverRow>
					<thead>
						<tr>
							<th>Estimate #</th>
							<th>Customer Name</th>
							<th>Date</th>
							<th>Amount</th>
							<th>Description</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{thisRows?.map((x) => {
							return (
								<tr style={{ cursor: "pointer" }} key={x.InvoiceHeaderID}>
									<td>{x.InvoiceNumber}</td>
									<td>{x.CustomerName}</td>
									<td>{new Date(x.InvoiceDate).toLocaleDateString()}</td>
									<td>${x.TotalAmount.toFixed(2)}</td>
									<td style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{x.Description}
									</td>
									<td>
										<WrappedButton
											busy={loadingPdfId === x.InvoiceHeaderID}
											onClick={() => {
												openPdf(x);
											}}>
											Open PDF
										</WrappedButton>
										<WrappedButton
											busy={hidingId === x.InvoiceHeaderID}
											color="danger"
											variant="soft"
											sx={{ marginLeft: 1 }}
											onClick={() => {
												hideProposal(x);
											}}>
											Hide Proposal
										</WrappedButton>
									</td>
								</tr>
							);
						})}
					</tbody>
					<tfoot>
						<tr>
							<td colSpan={6}>
								<div style={{ float: "right" }}>
									<span>
										<KeyboardArrowLeftIcon
											onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>{" "}
										{`Page ${pageNumber + 1} of ${maxPages}`}{" "}
										<KeyboardArrowRightIcon
											onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)}
											sx={{ verticalAlign: "middle", cursor: "pointer" }}
										/>
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				</Table>
			</Box>
		</>
	);
}
